
import type { TarifaEnergia } from '../../types';

// ==========================================
// COMMISSION TIER CONSTANTS
// ==========================================

const PARCEIRO_FAIXAS = [
  { min: 0, max: 5000, percentual: 15 },
  { min: 5001, max: 10000, percentual: 35 },
  { min: 10001, max: 25000, percentual: 50 },
  { min: 25001, max: 50000, percentual: 60 },
  { min: 50001, max: 80000, percentual: 70 },
  { min: 80001, max: Infinity, percentual: 80 },
] as const;

const VENDEDOR_FAIXAS = [
  { min: 0, max: 99.99, percentual: 7 },
  { min: 100, max: 119.99, percentual: 10 },
  { min: 120, max: 149.99, percentual: 12 },
  { min: 150, max: Infinity, percentual: 15 },
] as const;

const BONUS_FAIXAS = [
  { min: 1500, max: 7999, valor: 150 },
  { min: 8000, max: Infinity, valor: 600 },
] as const;

const GRUPOS_TARIFARIOS_A = ['A1', 'A2', 'A3', 'A3A', 'A4', 'AS'];

const PARCELA_1_PERCENTUAL = 0.30;
const PARCELA_2_PERCENTUAL = 0.70;

// ==========================================
// COMMISSION CALCULATION FUNCTIONS
// ==========================================

/**
 * Returns the commission % for a partner based on the negotiation's kWh consumption.
 * Uses per-negotiation volume (not aggregated monthly).
 */
export function getComissaoFaixaParceiro(consumoKwh: number): number {
  const faixa = PARCEIRO_FAIXAS.find(f => consumoKwh >= f.min && consumoKwh <= f.max);
  return faixa?.percentual ?? 15;
}

/**
 * Returns the commission % for a seller based on how much of their monthly goal they achieved.
 * @param percentualAtingimento - e.g. 110 means 110% of goal
 */
export function getComissaoPercentualVendedor(percentualAtingimento: number): number {
  const faixa = VENDEDOR_FAIXAS.find(f => percentualAtingimento >= f.min && percentualAtingimento <= f.max);
  return faixa?.percentual ?? 7;
}

/**
 * Returns the bonus amount (R$) for a single negotiation based on its kWh.
 * - 1,500–7,999 kWh → R$ 150
 * - 8,000+ kWh → R$ 600
 * - Below 1,500 → R$ 0
 */
export function getBonusVendedor(consumoKwh: number): number {
  const faixa = BONUS_FAIXAS.find(f => consumoKwh >= f.min && consumoKwh <= f.max);
  return faixa?.valor ?? 0;
}

/**
 * Checks if a tariff group is in the "A" category (A1–A4, AS).
 * These require manager approval and manual commission calculation.
 */
export function isGrupoTarifarioA(grupoTarifario: string | null | undefined): boolean {
  if (!grupoTarifario) return false;
  return GRUPOS_TARIFARIOS_A.includes(grupoTarifario.trim().toUpperCase());
}

/**
 * Returns the active tariff for a given reference date.
 * Finds the most recent tariff whose vigencia_inicio <= referenceDate.
 * Tariffs must be sorted by vigencia_inicio DESC.
 */
export function getTarifaVigente(
  tarifas: TarifaEnergia[],
  dataReferencia: string
): TarifaEnergia | null {
  const refDate = new Date(dataReferencia);
  refDate.setHours(0, 0, 0, 0);

  const sorted = [...tarifas].sort(
    (a, b) => new Date(b.vigencia_inicio).getTime() - new Date(a.vigencia_inicio).getTime()
  );

  for (const tarifa of sorted) {
    const vigencia = new Date(tarifa.vigencia_inicio);
    vigencia.setHours(0, 0, 0, 0);
    if (vigencia <= refDate) return tarifa;
  }

  return sorted.length > 0 ? sorted[sorted.length - 1] : null;
}

/**
 * Calculates the 5th business day after a given date.
 * Skips weekends (Saturday/Sunday). Does NOT consider holidays.
 */
export function calcularQuintoDiaUtil(dataBase: Date): Date {
  const result = new Date(dataBase);
  let diasUteis = 0;

  while (diasUteis < 5) {
    result.setDate(result.getDate() + 1);
    const dayOfWeek = result.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      diasUteis++;
    }
  }

  return result;
}

/**
 * Full partner commission calculation for a single negotiation.
 */
export function calcularComissaoParceiro(
  consumoMedioKwh: number,
  tarifaValor: number
): {
  faixaPercentual: number;
  valorBase: number;
  parcela1: number;
  parcela2: number;
} {
  const faixaPercentual = getComissaoFaixaParceiro(consumoMedioKwh);
  const valorBase = consumoMedioKwh * tarifaValor * (faixaPercentual / 100);
  const parcela1 = valorBase * PARCELA_1_PERCENTUAL;
  const parcela2 = valorBase * PARCELA_2_PERCENTUAL;

  return { faixaPercentual, valorBase, parcela1, parcela2 };
}

/**
 * Full seller commission calculation for a month.
 */
export function calcularComissaoVendedor(
  totalKwhVendido: number,
  metaKwh: number,
  tarifaValor: number,
  salarioBase: number,
  negociacoesConsumos: number[]
): {
  percentualAtingimento: number;
  percentualComissao: number;
  valorComissaoKwh: number;
  valorBonus: number;
  valorTotal: number;
} {
  const percentualAtingimento = metaKwh > 0 ? (totalKwhVendido / metaKwh) * 100 : 0;
  const percentualComissao = getComissaoPercentualVendedor(percentualAtingimento);
  const valorComissaoKwh = totalKwhVendido * tarifaValor * (percentualComissao / 100);
  // Bônus só é concedido quando o vendedor bate a meta (≥100%)
  const valorBonus = percentualAtingimento >= 100
    ? negociacoesConsumos.reduce((sum, c) => sum + getBonusVendedor(c), 0)
    : 0;
  const valorTotal = salarioBase + valorComissaoKwh + valorBonus;

  return {
    percentualAtingimento,
    percentualComissao,
    valorComissaoKwh,
    valorBonus,
    valorTotal,
  };
}

// ==========================================
// FORMATTING HELPERS
// ==========================================

export function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

export function formatKwh(value: number): string {
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh`;
}

/**
 * Returns the label for a partner commission tier.
 */
export function getFaixaLabel(consumoKwh: number): string {
  if (consumoKwh <= 5000) return '0 – 5.000 kWh';
  if (consumoKwh <= 10000) return '5.001 – 10.000 kWh';
  if (consumoKwh <= 25000) return '10.001 – 25.000 kWh';
  if (consumoKwh <= 50000) return '25.001 – 50.000 kWh';
  if (consumoKwh <= 80000) return '50.001 – 80.000 kWh';
  return 'Acima de 80.000 kWh';
}

export { PARCEIRO_FAIXAS, VENDEDOR_FAIXAS, BONUS_FAIXAS, GRUPOS_TARIFARIOS_A };
